import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AppContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SELECTED_PROJECT_KEY = 'selectedProjectId';
const PRIVATE_MODE_KEY = 'dashboardPrivateMode';

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProjectState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [privateMode, setPrivateModeState] = useState(() => {
    try { return localStorage.getItem(PRIVATE_MODE_KEY) === '1'; } catch (e) { return false; }
  });
  const [privateOrder, setPrivateOrder] = useState([]);

  const setPrivateMode = useCallback((on) => {
    setPrivateModeState(on);
    try { localStorage.setItem(PRIVATE_MODE_KEY, on ? '1' : '0'); } catch (e) { /* ignore */ }
  }, []);

  // Wrapper that also persists the selected project id to localStorage
  const setSelectedProject = useCallback((project) => {
    setSelectedProjectState(project);
    try {
      if (project && project.id) {
        localStorage.setItem(SELECTED_PROJECT_KEY, project.id);
      } else {
        localStorage.removeItem(SELECTED_PROJECT_KEY);
      }
    } catch (e) {
      // localStorage may be unavailable (private mode); ignore silently
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      // After user is loaded, fetch projects
      await fetchProjects();
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      setProjects(response.data);
      
      // Reconcile selection with persisted projectId before falling back to first
      if (response.data.length > 0 && !selectedProject) {
        let projectToSelect = null;
        try {
          const savedId = localStorage.getItem(SELECTED_PROJECT_KEY);
          if (savedId) {
            projectToSelect = response.data.find((p) => p.id === savedId) || null;
          }
        } catch (e) {
          // ignore localStorage failures
        }

        if (!projectToSelect) {
          // Fallback: first project
          projectToSelect = response.data[0];
        }
        setSelectedProject(projectToSelect);
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      return [];
    } finally {
      setProjectsLoading(false);
    }
  }, [selectedProject, setSelectedProject]);

  const addProject = useCallback((newProject) => {
    setProjects(prev => [...prev, newProject]);
    // Auto-select the newly created project
    setSelectedProject(newProject);
  }, []);

  const updateProject = useCallback((updatedProject) => {
    setProjects(prev => prev.map(p => 
      p.id === updatedProject.id ? updatedProject : p
    ));
    // Update selected project if it's the one being edited
    if (selectedProject?.id === updatedProject.id) {
      setSelectedProject(updatedProject);
    }
  }, [selectedProject]);

  const removeProject = useCallback((projectId) => {
    setProjects(prev => prev.filter(p => p.id !== projectId));
    // If deleted project was selected, select another one
    if (selectedProject?.id === projectId) {
      setProjects(prev => {
        if (prev.length > 0) {
          setSelectedProject(prev[0]);
        } else {
          setSelectedProject(null);
        }
        return prev;
      });
    }
  }, [selectedProject, setSelectedProject]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    // Fetch projects after login
    fetchProjects();
  };

  const logout = () => {
    localStorage.removeItem('token');
    try {
      localStorage.removeItem(SELECTED_PROJECT_KEY);
    } catch (e) {
      // ignore
    }
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setProjects([]);
    setSelectedProjectState(null);
  };

  const value = {
    user,
    setUser,
    projects,
    setProjects,
    projectsLoading,
    selectedProject,
    setSelectedProject,
    loading,
    login,
    logout,
    fetchProjects,
    addProject,
    updateProject,
    removeProject,
    privateMode,
    setPrivateMode,
    privateOrder,
    setPrivateOrder
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
